import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { withAuthInfo } from "@propelauth/react";
import {
  setTag as sentrySetTag,
  setUser as sentrySetUser,
} from "@sentry/react";
import Pusher from "pusher-js";
import React from "react"
import { ErrorBoundary } from "react-error-boundary";
import { RelayEnvironmentProvider, useFragment,useLazyLoadQuery } from "react-relay";
import { graphql } from "relay-runtime";
import config from "src/common/config";
import isDev from "src/common/isDev";
import { createRelayEnvironment, OrganizationSuspendedError, SUSPENDED_ORG_ERROR_MESSAGE, updateAuthToken } from "src/common/relay-env";
import { getRelayErrorMessage } from "src/common/utils";
import { EmbeddedChat } from "src/components/embedded-chat";
import { OrganizationSelectionModal } from "src/components/OrganizationSelectionModal";
import { SuspendedOrganizationDialog } from "src/components/SuspendedOrganizationDialog";
import { SuspendedOrganizationSplash } from "src/components/SuspendedOrganizationSplash";
import { TeamSelectionModal } from "src/components/TeamSelectionModal";
import { PageSuspense } from "src/components/ui/page-suspense";
import { TooltipProvider } from "src/components/ui/tooltip";
import { EmbeddedChatProvider } from "src/context/EmbeddedChatContext";
import { MeProvider } from "src/context/MeContext";
import { PusherProvider } from "src/context/PusherContext";
import { useTheme } from "src/context/ThemeContext";
import { useTeamSelection } from "src/hooks/use-team-selection";
import HomePage, { POST_LOGIN_REDIRECT_KEY } from "src/pages/HomePage";
import TierZeroAppRouter from "src/router";

import type { appMe$data } from "./__generated__/appMe.graphql";
import { appQuery as appQueryType } from "./__generated__/appQuery.graphql";

export type MeUserDataType = appMe$data;

export const appMeFragment = graphql`
  fragment appMe on GraphQLUser @relay(plural: false) {
    id
    email
    firstName
    lastName
    pictureUrl
    isSuperAdmin
    role
    scopes
    organization {
      id
      name
      brandTheme
      isSuspended
      suspensionReason
      suspendedAt
      creditLimitResetAt
      appUrlDomain
      onboardingCompletedAt
      enabledFeatureFlags
    }
    teamContext {
      id
      team {
        id
        name
      }
    }
  }
`;

const appQuery = graphql`
  query appQuery {
    me {
      ...appMe
      email
      firstName
      lastName
      organization {
        id
      }
    }
  }
`;

function AppContent() {
  const { resolvedTheme } = useTheme();

  React.useEffect(() => {
    if (isDev) {
      return;
    }
    amplitude.setUserId(me.email);
    if (me.isSuperAdmin) {
      amplitude.setOptOut(true);
    }
    const identify = new amplitude.Identify();
    identify.set("theme", resolvedTheme);
    amplitude.identify(identify);
    amplitude.track("Page Load");
    amplitude.flush();
  }, []);

  let me: appMe$data;
  const data = useLazyLoadQuery<appQueryType>(appQuery, {});
  me = useFragment(appMeFragment, data.me) as appMe$data;

  // Use team selection hook with backend context
  const { teamId, selectTeam } = useTeamSelection({
    userId: me.id,
    initialTeamContext: me.teamContext,
  });

  const pusherRef = React.useRef<Pusher | null>(null);
  if (!pusherRef.current) {
    pusherRef.current = new Pusher(config.get("PUSHER_KEY"), {
      cluster: config.get("PUSHER_CLUSTER"),
    });

    // // Add connection state logging
    // pusherRef.current.connection.bind("state_change", () => {
    //   // Connection state changed
    // });

    // pusherRef.current.connection.bind("connected", () => {
    //   // Connected successfully
    // });

    // pusherRef.current.connection.bind("disconnected", () => {
    //   console.warn("[Pusher] Disconnected:", {
    //     timestamp: new Date().toISOString(),
    //   });
    // });

    // pusherRef.current.connection.bind("error", (error: any) => {
    //   console.error("[Pusher] Connection error:", {
    //     error,
    //     timestamp: new Date().toISOString(),
    //   });
    // });
  }
  const pusher = pusherRef.current;

  // Track when team selection is in progress to prevent modal from closing prematurely
  // Must be before any early returns to maintain consistent hook order
  const [isSelectingTeam, setIsSelectingTeam] = React.useState(false);
  const [suspendedDialogDismissed, setSuspendedDialogDismissed] = React.useState(false);

  // Organization should always be defined for authenticated users
  // During navigation transitions, organization may momentarily be undefined - show loading state
  if (!me.organization || !me.organization.id) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-midnight">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-steel border-t-electric" />
      </div>
    );
  }

  const organization = me.organization;
  const isSuspended = Boolean(organization.isSuspended);
  const isExtensionRefresh = window.location.pathname === "/extension/refresh";
  const showSuspendedDialog = isSuspended && !suspendedDialogDismissed && !isExtensionRefresh;

  // Redirect to organization's app URL if current origin doesn't match
  const appUrlDomain = organization.appUrlDomain;
  if (appUrlDomain) {
    const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
    const enableRedirectInDev = import.meta.env.VITE_ENABLE_DOMAIN_REDIRECT === "true";

    try {
      const currentUrl = new URL(window.location.origin);
      const targetUrl = new URL(appUrlDomain);
      const sameHostAndPort =
        currentUrl.hostname === targetUrl.hostname &&
        (currentUrl.port || "") === (targetUrl.port || "");

      if (!isExtensionRefresh && !sameHostAndPort && (!isLocalhost || enableRedirectInDev)) {
        window.location.href = `${appUrlDomain}${window.location.pathname}${window.location.search}${window.location.hash}`;
        return null;
      }
    } catch {
      // If appUrlDomain is malformed, fail open and don't redirect
    }
  }

  sentrySetUser({
    email: me.email,
    id: me.id,
  });
  sentrySetTag("organization", organization.id);

  // Check if onboarding is complete - redirect to onboarding if not.
  // Allow previewing ingested resources and memory from the onboarding Review step.
  const isOnboardingPage = window.location.pathname.startsWith("/onboarding");
  const isOnboardingPreviewPage =
    window.location.pathname === "/context/resources" ||
    window.location.pathname === "/context/snippets";
  if (
    !organization.onboardingCompletedAt &&
    !isOnboardingPage &&
    !isOnboardingPreviewPage &&
    !isSuspended
  ) {
    window.location.href = "/onboarding";
    return null;
  }

  // Handle team selection
  const handleTeamSelect = async (id: string, name: string, isNewTeam?: boolean) => {
    // Keep modal visible during selection to prevent flash of app content
    setIsSelectingTeam(true);
    const success = await selectTeam(id, name);
    if (success) {
      if (isNewTeam) {
        // Redirect to onboarding for newly created teams
        window.location.href = "/onboarding";
      } else {
        window.location.reload();
      }
    } else {
      setIsSelectingTeam(false);
    }
  };
  // Skip team selection for Slack connect page - users need to connect first, then select team
  const isSlackConnectPage = window.location.pathname.startsWith("/connect/slack/");
  // Show modal if no team selected OR if selection is in progress (prevents optimistic update from closing it)
  // Also skip during onboarding - team will be created as part of the onboarding flow
  const showTeamSelectionModal = (!teamId || isSelectingTeam) && !isSlackConnectPage && !isOnboardingPage;

  return (
    <TooltipProvider>
      <MeProvider me={me}>
        <PusherProvider pusherClient={pusher}>
          <EmbeddedChatProvider>
            {/* Show mandatory team selection modal if no team is selected */}
            <TeamSelectionModal
              isOpen={showTeamSelectionModal}
              organizationId={organization.id}
              onSelectTeam={handleTeamSelect}
            />
            <SuspendedOrganizationDialog
              isOpen={showSuspendedDialog}
              onDismiss={() => setSuspendedDialogDismissed(true)}
              suspensionReason={organization.suspensionReason ?? null}
              creditLimitResetAt={organization.creditLimitResetAt ?? null}
              suspendedAt={organization.suspendedAt ?? null}
            />
            <TierZeroAppRouter me={me} />
            <EmbeddedChat />
          </EmbeddedChatProvider>
        </PusherProvider>
      </MeProvider>
    </TooltipProvider>
  );
}

const App = withAuthInfo((props) => {
  // If user is not logged in, show the landing page
  if (!props.isLoggedIn) {
    return <HomePage />;
  }

  // After login, redirect to the page the user originally tried to visit
  try {
    const raw = localStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    if (raw) {
      const { path, ts } = JSON.parse(raw) as { path: string; ts: number };
      const isFresh = Date.now() - ts < 3600000; // 1 hour TTL
      const isSafe = path.startsWith("/") && !path.startsWith("//");
      const isDifferent = path !== "/" && path !== window.location.pathname + window.location.search + window.location.hash;
      if (isFresh && isSafe && isDifferent) {
        window.location.href = path;
        return null;
      }
    }
  } catch { /* storage blocked or malformed */ }

  const orgs = props.orgHelper.getOrgs();
  const storedOrgId = localStorage.getItem("tz_current_org_id");
  const forceOrgSelection = localStorage.getItem("tz_debug_force_org_selection");

  // If there are multiple organizations (or debug flag is set) and no org is stored in localStorage, show the organization selection modal
  if (orgs && (orgs.length > 1 || forceOrgSelection) && !storedOrgId) {
    // Clear the debug flag so it doesn't persist after org selection
    if (forceOrgSelection) {
      localStorage.removeItem("tz_debug_force_org_selection");
    }
    return (
      <OrganizationSelectionModal
        organizations={orgs}
      />
    );
  }

  if (!isDev) {
    // Skip subdomain redirect for extension refresh to avoid breaking chrome.identity.launchWebAuthFlow
    const isExtensionRefresh = window.location.pathname === "/extension/refresh";

    if (!isExtensionRefresh && orgs && orgs.length > 0) {
      // Find the stored org or default to the first one
      const currentOrg = storedOrgId
        ? orgs.find(org => org.orgId === storedOrgId) || orgs[0]
        : orgs[0];
      const orgMetadata = currentOrg.orgMetadata;
      if (orgMetadata) {
        const subdomains = orgMetadata["subdomains"];
        if (subdomains && subdomains.length > 0) {
          const subdomain = subdomains[0];
          // eslint-disable-next-line custom/no-hardcoded-brand-name -- hardcoded URL
          if (`${subdomain}.tierzero.ai` !== window.location.hostname) {
            // eslint-disable-next-line custom/no-hardcoded-brand-name -- hardcoded URL
            window.location.href = `https://${subdomain}.tierzero.ai${window.location.pathname}${window.location.search}`;
          }
        }
      }
    }
  }

  const relayEnv = React.useMemo(
    () => createRelayEnvironment(props.accessToken),
    [],
  );

  React.useEffect(() => {
    updateAuthToken(props.accessToken);
  }, [props.accessToken]);

  React.useEffect(() => {
    if (isDev) {
      return;
    }
    amplitude.init("d686655ff95768100c7c21a3d351cd61", { autocapture: true });
    amplitude.add(
      sessionReplayPlugin({
        sampleRate: 1,
      }),
    );
  }, []);

  // @ts-ignore: React 18 and react-relay type compatibility issue
  return (
    // @ts-ignore
    <RelayEnvironmentProvider environment={relayEnv}>
      <PageSuspense>
        <ErrorBoundary
          fallbackRender={(e) => {
            // Check for organization suspended error in multiple ways:
            // 1. Direct instance check
            // 2. Error name check (in case error was serialized/wrapped)
            // 3. Error message check (Relay may wrap the error)
            const isSuspendedError =
              e.error instanceof OrganizationSuspendedError ||
              e.error?.name === "OrganizationSuspendedError" ||
              e.error?.message?.includes(SUSPENDED_ORG_ERROR_MESSAGE) ||
              (Array.isArray((e.error as any)?.source?.errors) &&
                (e.error as any).source.errors.some(
                  (err: any) => err.message?.includes(SUSPENDED_ORG_ERROR_MESSAGE)
                )) ||
              (e.error as any)?.cause?.name === "OrganizationSuspendedError" ||
              (e.error as any)?.cause?.message?.includes(SUSPENDED_ORG_ERROR_MESSAGE);

            if (isSuspendedError) {
              // Extract brandTheme from the error if available
              const brandTheme = (e.error as OrganizationSuspendedError)?.brandTheme ?? null;
              return <SuspendedOrganizationSplash brandTheme={brandTheme} />;
            }

            // Auto-reload on chunk load failures from stale deployments
            if (e.error?.message?.includes("Failed to fetch dynamically imported module")) {
              window.location.reload();
              return null;
            }

            const errorMessage = isDev
              ? getRelayErrorMessage(e.error)
              : "A problem occurred. Please try again later.";

            return (
              <div className="flex h-screen items-center justify-center p-4">
                <div className="max-w-md p-6 rounded-lg bg-card text-card-foreground border border-border shadow-lg">
                  <h2 className="text-xl font-semibold text-destructive mb-3">Application Error</h2>
                  <p className="text-muted-foreground">{errorMessage}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Reload Application
                  </button>
                </div>
              </div>
            );
          }}
        >
          <AppContent />
        </ErrorBoundary>
      </PageSuspense>
    </RelayEnvironmentProvider>
  );
});

export default App;
